export const NAB_CASE_TAXONOMY_CONFIG = {
  id: 'nab_accountability',
  label: 'NAB / Accountability',
  shortLabel: 'NAB',
  description: 'NAB inquiries, investigations, call-up notices, accountability references, trials, bail, remand, voluntary return, plea bargain, freezing/attachment of property, appeals, constitutional challenges, and related accountability proceedings.',
  active: true,
  types: [
    {
      id: 'NAB Inquiry / Call-Up Notice Matter',
      label: 'NAB Inquiry / Call-Up Notice Matter',
      shortLabel: 'Inquiry / Call-Up',
      description: 'Pre-reference NAB inquiry matters, including call-up notice, questionnaire, document production, verification of complaint, inquiry reply, and representation before NAB.',
      legalBasis: [
        'National Accountability Ordinance, 1999, as amended.',
        'NAB inquiry and verification process.',
        'Call-up notices/questionnaires require factual and documentary response.',
        'Challenge to call-up notice may be considered through constitutional jurisdiction where maintainable.',
        'Fair process, jurisdiction, mala fide, fishing inquiry, harassment, and lack of nexus may be relevant depending facts.'
      ],
      subtypes: [
        'Call-Up Notice',
        'Inquiry Notice',
        'Questionnaire / Reply to NAB',
        'Document Production Notice',
        'Personal Appearance before NAB',
        'Verification of Complaint',
        'Inquiry Closure Request',
        'Representation before NAB',
        'Other NAB Inquiry Matter'
      ],
      forumOptions: [
        { label: 'NAB Regional Office / Investigation Wing', description: 'Common forum for call-up notice response, appearance, document production, and inquiry representation.', isRecommended: true },
        { label: 'Chairman NAB / NAB Headquarters', description: 'Use for administrative representation or inquiry closure request where appropriate.', isRecommended: false },
        { label: 'High Court / Constitutional Jurisdiction', description: 'Use where call-up notice/inquiry action is challenged by writ where maintainable.', isRecommended: false },
        { label: 'Other / Custom Forum', description: 'Use if forum differs.', isRecommended: false }
      ],
      sideOptions: ['Notice Recipient / Accused', 'Witness', 'Complainant', 'NAB / Prosecution', 'Affected Person', 'Applicant', 'Respondent'],
      documentTabs: [
        'All',
        'Call-Up Notice / Inquiry',
        'Reply / Representation',
        'Documents Submitted',
        'NAB Correspondence',
        'Orders / Directions',
        'Miscellaneous'
      ],
      defaultStages: [
        'Notice / Complaint Review',
        'Record Collection',
        'Reply / Representation Drafting',
        'Submission to NAB',
        'Personal Appearance, if required',
        'Further Questionnaire / Documents, if any',
        'Inquiry Proceedings / Verification',
        'Inquiry Closure / Conversion to Investigation / Further Action',
        'Constitutional Challenge, if any',
        'Closed'
      ],
      importantNotes: 'Inquiry forum depends on NAB region, notice issuing authority, stage of proceedings, maintainability of challenge, and nature of relief.',
      aiContextHints: 'AI should understand notice date, questionnaire, allegations, public office/entity link, documents requested, assets/transactions, reply strategy, jurisdiction objections, harassment/mala fide concerns, and closure request.',
      nabSpecificMetadataFields: ['nabRegion', 'bureauOffice', 'inquiryNumber', 'callUpNoticeDate', 'questionnaireDate', 'appearanceDate', 'accusedName', 'allegationType', 'inquiryStage']
    },
    {
      id: 'NAB Investigation Matter',
      label: 'NAB Investigation Matter',
      shortLabel: 'Investigation',
      description: 'NAB investigation stage matters after inquiry is converted into investigation, including summons, statements, document production, arrest apprehension, cooperation, investigation response, and investigation closure.',
      legalBasis: [
        'National Accountability Ordinance, 1999, as amended.',
        'CrPC/QSO principles may be relevant in investigation and evidence contexts where applicable.',
        'Arrest apprehension may trigger bail/protective remedy.',
        'Challenge to investigation may be through constitutional jurisdiction where maintainable.'
      ],
      subtypes: [
        'Investigation Notice',
        'Summons / Appearance',
        'Statement before NAB',
        'Document Production during Investigation',
        'Arrest Apprehension',
        'Investigation Closure Request',
        'Challenge to Investigation',
        'Other NAB Investigation Matter'
      ],
      forumOptions: [
        { label: 'NAB Regional Office / Investigation Wing', description: 'Common forum for investigation summons, statements, document production, and cooperation.', isRecommended: true },
        { label: 'Accountability Court', description: 'Use where investigation has resulted in reference or court-connected proceedings.', isRecommended: false },
        { label: 'High Court / Constitutional Jurisdiction', description: 'Use where investigation/action/arrest threat is challenged where maintainable.', isRecommended: false },
        { label: 'High Court', description: 'Use for pre-arrest/protective bail where applicable.', isRecommended: false },
        { label: 'Other / Custom Forum', description: 'Use if forum differs.', isRecommended: false }
      ],
      sideOptions: ['Accused / Suspect', 'Witness', 'Complainant', 'NAB / Prosecution', 'Affected Person', 'Applicant', 'Respondent'],
      documentTabs: [
        'All',
        'Investigation Notice / Summons',
        'Statements / Replies',
        'Documents Submitted',
        'NAB Correspondence',
        'Applications / Challenges',
        'Orders',
        'Miscellaneous'
      ],
      defaultStages: [
        'Investigation Notice / Summons Review',
        'Record Collection',
        'Statement / Reply Preparation',
        'Appearance before NAB',
        'Document Production',
        'Further Investigation / Follow-up Notices',
        'Arrest Apprehension / Bail Strategy, if any',
        'Investigation Closure / Reference Recommendation / Further Action',
        'Constitutional Challenge, if any',
        'Closed'
      ],
      importantNotes: 'Investigation forum depends on investigation stage, arrest apprehension, reference status, and whether court intervention is maintainable.',
      aiContextHints: 'AI should understand investigation number, allegations, notices, statements, documents produced, arrest apprehension, inquiry-to-investigation conversion, and potential reference risk.',
      nabSpecificMetadataFields: ['nabRegion', 'bureauOffice', 'investigationNumber', 'appearanceDate', 'accusedName', 'allegationType', 'investigationStage']
    },
    {
      id: 'NAB Reference / Trial',
      label: 'NAB Reference / Trial',
      shortLabel: 'Reference',
      description: 'Accountability Court proceedings after filing of NAB reference, including supply of record, charge, evidence, cross-examination, statement of accused, defence, arguments, judgment, conviction, acquittal, and related trial matters.',
      legalBasis: [
        'National Accountability Ordinance, 1999, as amended.',
        'Accountability Court trial jurisdiction.',
        'CrPC and Qanun-e-Shahadat may be relevant where applicable.',
        'NAB reference may involve corruption, corrupt practices, assets beyond means, misuse of authority, embezzlement, illegal gain, public office, company/corporate transactions, or public funds.',
        'Burden/evidentiary rules may be special under accountability law; avoid overstatement.'
      ],
      subtypes: [
        'NAB Reference',
        'Corruption Reference',
        'Assets Beyond Means Reference',
        'Misuse of Authority Reference',
        'Embezzlement / Misappropriation Reference',
        'Illegal Gain / Kickbacks / Commission Reference',
        'Public Office Holder Reference',
        'Company / Corporate Reference',
        'Supplementary Reference',
        'Reference After Investigation',
        'Other Accountability Reference'
      ],
      forumOptions: [
        { label: 'Accountability Court', description: 'Primary trial forum for NAB references.', isRecommended: true },
        { label: 'High Court', description: 'Use for bail, appeal, transfer/challenge, or constitutional jurisdiction where maintainable.', isRecommended: false },
        { label: 'Supreme Court', description: 'Use where further appellate/special jurisdiction is invoked according to law.', isRecommended: false },
        { label: 'Other / Custom Forum', description: 'Use if forum differs.', isRecommended: false }
      ],
      sideOptions: ['Accused', 'Co-Accused', 'NAB / Prosecution', 'Complainant', 'Witness', 'Affected Person'],
      documentTabs: [
        'All',
        'Reference / Charge',
        'NAB Record',
        'Prosecution Evidence',
        'Defence Evidence',
        'Applications',
        'Orders & Judgments',
        'Miscellaneous'
      ],
      defaultStages: [
        'Reference Filed',
        'Court Appearance / Summons / Warrants',
        'Supply of Reference / Record',
        'Charge / Charge Framing',
        'Prosecution Evidence',
        'Cross-Examination',
        'Statement of Accused',
        'Defence Evidence, if any',
        'Final Arguments',
        'Judgment',
        'Conviction / Acquittal',
        'Sentence / Fine / Forfeiture, if any',
        'Appeal / Bail / Suspension, if any',
        'Closed'
      ],
      importantNotes: 'Reference forum depends on the Accountability Court having jurisdiction, reference filing, accused status, and any transfer/challenge/appeal.',
      aiContextHints: 'AI should understand reference allegations, accused roles, prosecution documents, assets beyond means analysis, misuse of authority, financial trail, witness evidence, cross-examination, defence explanation, judgment, and appeal grounds.',
      nabSpecificMetadataFields: ['accountabilityCourtCaseNumber', 'referenceNumber', 'accusedName', 'coAccusedNames', 'allegationType', 'referenceFilingDate', 'chargeFramingDate', 'convictionDate']
    },
    {
      id: 'NAB Bail',
      label: 'NAB Bail',
      shortLabel: 'Bail',
      description: 'Bail matters in NAB/accountability cases, including pre-arrest bail, post-arrest bail, protective bail, bail after conviction, bail cancellation, suspension of sentence, and bail pending appeal.',
      legalBasis: [
        'National Accountability Ordinance, 1999, as amended.',
        'Constitutional jurisdiction / High Court bail jurisdiction where applicable.',
        'Post-arrest bail, pre-arrest bail, protective bail, and suspension of sentence depend on arrest/custody/reference/appeal status.',
        'Grounds may include further inquiry, hardship, delay, mala fide, no useful purpose of custody, health, evidence already collected, and merits depending facts.'
      ],
      subtypes: [
        'Pre-Arrest Bail',
        'Post-Arrest Bail',
        'Protective Bail',
        'Bail After Conviction',
        'Bail Cancellation',
        'Suspension of Sentence',
        'Bail Pending Appeal',
        'Other NAB Bail Matter'
      ],
      forumOptions: [
        { label: 'High Court', description: 'Common forum for NAB bail matters, subject to current law and maintainability.', isRecommended: true },
        { label: 'Accountability Court', description: 'Use where bail/custody issue is before trial court or reference-connected forum where applicable.', isRecommended: false },
        { label: 'Supreme Court', description: 'Use where further bail/appeal jurisdiction is invoked according to law.', isRecommended: false },
        { label: 'Other / Custom Forum', description: 'Use if forum differs.', isRecommended: false }
      ],
      sideOptions: ['Accused / Applicant', 'NAB / Prosecution', 'Complainant', 'State', 'Respondent'],
      documentTabs: [
        'All',
        'Bail Petition',
        'NAB Record',
        'Medical / Personal Grounds',
        'Applications',
        'Orders',
        'Miscellaneous'
      ],
      defaultStages: [
        'Case / Arrest Risk Review',
        'Bail Petition Drafting',
        'Filing before Forum',
        'Interim / Protective Relief, if any',
        'Notice to NAB / State',
        'NAB Reply / Comments',
        'Record Produced / Called',
        'Arguments',
        'Bail Order',
        'Surety / Bonds / Release, if granted',
        'Confirmation / Dismissal / Disposal',
        'Further Remedy, if any',
        'Closed'
      ],
      importantNotes: 'NAB bail forum depends on arrest/custody status, reference stage, conviction/appeal status, and current maintainability law.',
      aiContextHints: 'AI should understand arrest status, custody period, remand history, allegations, amount involved, evidence collected, health/hardship, delay, inquiry/reference status, bail grounds, and surety/release.',
      nabSpecificMetadataFields: ['arrestDate', 'custodyStatus', 'accusedName', 'allegationType', 'accountabilityCourtCaseNumber', 'linkedReferenceCaseId']
    },
    {
      id: 'Remand / Custody Matter',
      label: 'Remand / Custody Matter',
      shortLabel: 'Remand',
      description: 'Matters relating to physical remand, judicial remand, custody, production before court, extension/refusal of remand, medical examination, custody transfer, and remand-related proceedings in NAB cases.',
      legalBasis: [
        'National Accountability Ordinance, 1999, as amended.',
        'Accountability Court/remand jurisdiction.',
        'Custody/remand legality depends on arrest memo, grounds, investigation need, production timing, custody period, and rights of accused.'
      ],
      subtypes: [
        'Physical Remand',
        'Judicial Remand',
        'Extension of Remand',
        'Challenge to Remand',
        'Production Order',
        'Medical Examination during Custody',
        'Custody Transfer',
        'Other Remand / Custody Matter'
      ],
      forumOptions: [
        { label: 'Accountability Court / Remand Court', description: 'Common forum for NAB remand/custody production and remand extension.', isRecommended: true },
        { label: 'Accountability Court', description: 'Use where custody issue is connected with reference/trial.', isRecommended: false },
        { label: 'High Court / Constitutional Jurisdiction', description: 'Use where custody/remand/arrest is challenged where maintainable.', isRecommended: false },
        { label: 'Other / Custom Forum', description: 'Use if forum differs.', isRecommended: false }
      ],
      sideOptions: ['Accused', 'NAB / Prosecution', 'Applicant', 'Respondent', 'Family Member / Representative'],
      documentTabs: [
        'All',
        'Remand Application / Order',
        'Arrest / Custody Record',
        'Medical / Welfare Record',
        'Applications',
        'Orders',
        'Miscellaneous'
      ],
      defaultStages: [
        'Arrest / Custody Review',
        'Production before Court',
        'NAB Remand Request',
        'Defence Opposition to Remand',
        'Physical Remand Granted / Refused',
        'Extension Request, if any',
        'Judicial Remand',
        'Medical / Welfare Application, if any',
        'Bail / Challenge to Remand, if any',
        'Further Production / Reference Stage',
        'Closed'
      ],
      importantNotes: 'Remand forum depends on arrest, production, custody status, investigation needs, and legality of remand order.',
      aiContextHints: 'AI should understand arrest date, production date, remand days requested/granted, grounds for remand, investigation need, accused health, custody legality, and next production date.',
      nabSpecificMetadataFields: ['arrestDate', 'custodyStatus', 'remandStartDate', 'remandEndDate', 'remandDaysRequested', 'remandDaysGranted', 'accusedName', 'allegationType']
    },
    {
      id: 'Voluntary Return / Plea Bargain',
      label: 'Voluntary Return / Plea Bargain',
      shortLabel: 'VR / Plea Bargain',
      description: 'Matters relating to voluntary return, plea bargain, settlement, recovery, approval, implementation, challenge, or legal consequences of voluntary return/plea bargain in accountability proceedings.',
      legalBasis: [
        'National Accountability Ordinance, 1999, as amended.',
        'Voluntary return / plea bargain provisions and approval process.',
        'Legal consequences/disqualification may apply depending current law and order.',
        'Plea bargain terms, recovery amount, approval forum, and compliance are central.'
      ],
      subtypes: [
        'Voluntary Return',
        'Plea Bargain',
        'Plea Bargain Approval',
        'Plea Bargain Rejection',
        'Settlement / Recovery Proposal',
        'Implementation of Plea Bargain',
        'Challenge to Plea Bargain',
        'Consequences / Disqualification Issue',
        'Other VR / Plea Bargain Matter'
      ],
      forumOptions: [
        { label: 'NAB Regional Office / Investigation Wing', description: 'Use for voluntary return/settlement proposal and NAB processing before approval.', isRecommended: true },
        { label: 'Chairman NAB / NAB Headquarters', description: 'Use where approval/administrative processing is involved.', isRecommended: false },
        { label: 'Accountability Court', description: 'Use where plea bargain requires court approval or reference-connected order.', isRecommended: false },
        { label: 'High Court / Constitutional Jurisdiction', description: 'Use where plea bargain/order/consequence is challenged where maintainable.', isRecommended: false },
        { label: 'Other / Custom Forum', description: 'Use if forum differs.', isRecommended: false }
      ],
      sideOptions: ['Accused / Applicant', 'NAB / Prosecution', 'Affected Person', 'Respondent', 'Guarantor / Surety'],
      documentTabs: [
        'All',
        'VR / Plea Bargain Application',
        'Recovery / Settlement Record',
        'NAB Approval Record',
        'Court Orders',
        'Correspondence',
        'Miscellaneous'
      ],
      defaultStages: [
        'Liability / Recovery Assessment',
        'VR / Plea Bargain Proposal',
        'Submission to NAB',
        'Negotiation / Verification',
        'Approval Process',
        'Court Approval, if required',
        'Payment / Recovery Implementation',
        'Compliance / Consequences',
        'Challenge / Modification, if any',
        'Closed'
      ],
      importantNotes: 'VR/plea bargain forum depends on stage, approval authority, reference status, amount, compliance, and legal consequences.',
      aiContextHints: 'AI should understand proposed amount, alleged liability, recovery calculation, payment schedule, approval status, court order, compliance, and consequences/disqualification issue.',
      nabSpecificMetadataFields: ['pleaBargainAmount', 'voluntaryReturnAmount', 'recoveryAmount', 'accusedName', 'allegationType']
    },
    {
      id: 'Freezing / Attachment of Property',
      label: 'Freezing / Attachment of Property',
      shortLabel: 'Freezing / Attachment',
      description: 'Matters relating to freezing, seizure, attachment, custody, release, objection, or challenge concerning property, bank accounts, assets, vehicles, records, or third-party interests in NAB proceedings.',
      legalBasis: [
        'National Accountability Ordinance, 1999, as amended.',
        'Freezing/attachment/seizure powers and court confirmation/challenge context may apply.',
        'Third-party ownership, bona fide purchaser, family property, business accounts, and proportionality may be relevant.'
      ],
      subtypes: [
        'Freezing of Bank Account',
        'Freezing of Immovable Property',
        'Attachment of Property',
        'Seizure of Record / Assets',
        'Release of Frozen Property',
        'Objection to Freezing / Attachment',
        'Third-Party Claim / Objection',
        'Vehicle / Asset Custody',
        'Other Freezing / Attachment Matter'
      ],
      forumOptions: [
        { label: 'NAB Regional Office / Investigation Wing', description: 'Use for representation/request before NAB regarding freezing/seizure/property record.', isRecommended: true },
        { label: 'Accountability Court', description: 'Use where freezing/attachment is connected with reference/trial or court order.', isRecommended: false },
        { label: 'High Court / Constitutional Jurisdiction', description: 'Use where freezing/attachment is challenged by writ where maintainable.', isRecommended: false },
        { label: 'Other / Custom Forum', description: 'Use if forum differs.', isRecommended: false }
      ],
      sideOptions: ['Accused', 'Property Owner', 'Third-Party Claimant', 'NAB / Prosecution', 'Affected Person', 'Applicant', 'Respondent'],
      documentTabs: [
        'All',
        'Freezing / Attachment Application',
        'Property / Asset Record',
        'Bank / Financial Record',
        'Objections / Replies',
        'Orders',
        'Miscellaneous'
      ],
      defaultStages: [
        'Freezing / Attachment Order Review',
        'Ownership / Asset Record Collection',
        'Application / Objection Drafting',
        'Filing / Submission',
        'Notice to NAB / Parties',
        'NAB Reply / Comments',
        'Record / Ownership Evidence',
        'Arguments',
        'Release / Confirmation / Rejection Order',
        'Compliance / Further Remedy',
        'Closed'
      ],
      importantNotes: 'Freezing/attachment forum depends on whether order was issued by NAB, court confirmation/status, property ownership, third-party rights, and pending reference/investigation.',
      aiContextHints: 'AI should understand asset type, owner, account/property details, freezing order, link to allegation, third-party claim, source of funds, business impact, and relief sought.',
      nabSpecificMetadataFields: ['propertyDetails', 'bankAccountDetails', 'frozenAssetsDetails', 'seizureMemoDetails', 'freezingOrderDate', 'accusedName', 'allegationType']
    },
    {
      id: 'NAB Appeal',
      label: 'NAB Appeal',
      shortLabel: 'Appeal',
      description: 'Appeal against Accountability Court judgment, conviction, acquittal, sentence, final order, bail order, freezing order, plea bargain order, or other appealable accountability order where maintainable.',
      legalBasis: [
        'National Accountability Ordinance, 1999, as amended.',
        'Appeal forum and limitation depend on impugned order and current law.',
        'High Court is commonly involved in appeals from Accountability Court judgments/orders where maintainable.',
        'Suspension of sentence/bail pending appeal may be relevant.'
      ],
      subtypes: [
        'Appeal Against Conviction',
        'Appeal Against Acquittal',
        'Appeal Against Sentence',
        'Appeal Against Final Judgment',
        'Appeal Against Bail Order',
        'Appeal Against Freezing / Attachment Order',
        'Appeal Against Plea Bargain Order',
        'Appeal by NAB',
        'Appeal by Accused',
        'Other NAB Appeal'
      ],
      forumOptions: [
        { label: 'High Court', description: 'Common appellate forum for accountability appeals where maintainable.', isRecommended: true },
        { label: 'Supreme Court', description: 'Use where further appeal/special jurisdiction is invoked according to law.', isRecommended: false },
        { label: 'Other / Custom Forum', description: 'Use if forum differs.', isRecommended: false }
      ],
      sideOptions: ['Appellant', 'Respondent', 'Accused', 'NAB / Prosecution', 'Complainant', 'Affected Person'],
      documentTabs: [
        'All',
        'Memo of Appeal',
        'Applications',
        'Impugned Judgment / Order',
        'Reference Record',
        'Orders',
        'Miscellaneous'
      ],
      defaultStages: [
        'Impugned Judgment / Order Review',
        'Certified Copies Awaited',
        'Memo of Appeal Drafting',
        'Filing',
        'Office Objections / Scrutiny',
        'Preliminary Hearing / Admission',
        'Notice to Respondent / NAB',
        'Record Summoned / Received',
        'Suspension of Sentence / Bail, if any',
        'Arguments',
        'Judgment / Final Order',
        'Further Remedy, if any',
        'Closed'
      ],
      importantNotes: 'NAB appeal forum depends on the impugned order, current accountability law, limitation, and whether appeal is maintainable.',
      aiContextHints: 'AI should understand impugned judgment, conviction/acquittal/sentence, reference record, grounds of appeal, evidence appreciation, suspension/bail, limitation, and relief sought.',
      nabSpecificMetadataFields: ['appealFilingDate', 'impugnedOrderDate', 'convictionDate', 'sentenceDetails', 'linkedReferenceCaseId', 'accusedName']
    },
    {
      id: 'NAB Revision / Constitutional Challenge',
      label: 'NAB Revision / Constitutional Challenge',
      shortLabel: 'Revision / Writ',
      description: 'Revision, writ, constitutional petition, or challenge against NAB action, call-up notice, inquiry, investigation, arrest, remand, reference, freezing order, accountability court order, or NAB authority action where maintainable.',
      legalBasis: [
        'National Accountability Ordinance, 1999, as amended.',
        'Constitutional jurisdiction where maintainable.',
        'Maintainability depends on alternate remedy, stage of inquiry/investigation/reference, jurisdictional defect, mala fide, fair process, and nature of impugned order/action.'
      ],
      subtypes: [
        'Writ Against NAB Call-Up Notice',
        'Writ Against NAB Inquiry',
        'Writ Against NAB Investigation',
        'Challenge to NAB Arrest',
        'Challenge to NAB Reference',
        'Challenge to Accountability Court Order',
        'Challenge to Freezing / Attachment',
        'Challenge to Jurisdiction',
        'Challenge to Transfer / Return of Reference',
        'Other NAB Constitutional Challenge'
      ],
      forumOptions: [
        { label: 'High Court / Constitutional Jurisdiction', description: 'Common forum for writ/constitutional challenge against NAB action where maintainable.', isRecommended: true },
        { label: 'High Court', description: 'Use where challenge is connected with bail, appeal, or accountability order.', isRecommended: false },
        { label: 'Supreme Court', description: 'Use where further constitutional/appellate jurisdiction is invoked according to law.', isRecommended: false },
        { label: 'Accountability Court', description: 'Use where the issue should first be raised before trial court/reference court.', isRecommended: false },
        { label: 'Other / Custom Forum', description: 'Use if forum differs.', isRecommended: false }
      ],
      sideOptions: ['Petitioner', 'Respondent', 'Accused', 'NAB / Prosecution', 'Affected Person', 'Applicant'],
      documentTabs: [
        'All',
        'Writ / Challenge Petition',
        'NAB Record',
        'Applications',
        'Impugned Notice / Order',
        'Replies / Comments',
        'Orders',
        'Miscellaneous'
      ],
      defaultStages: [
        'Impugned NAB Action / Order Review',
        'Maintainability / Alternate Remedy Review',
        'Petition Drafting',
        'Filing',
        'Office Objections / Scrutiny',
        'Preliminary Hearing / Admission',
        'Notice to NAB / Respondents',
        'Comments / Report',
        'Rejoinder, if any',
        'Interim Relief / Stay, if any',
        'Arguments',
        'Final Order',
        'Compliance / Further Remedy',
        'Closed'
      ],
      importantNotes: 'NAB writ/challenge maintainability depends on alternate remedy, stage of proceedings, jurisdiction, mala fide, fair process, and impugned action/order.',
      aiContextHints: 'AI should understand impugned NAB action, jurisdiction, inquiry/investigation/reference stage, alternate remedy, mala fide, fair process, freezing/arrest/remand issues, and relief sought.',
      nabSpecificMetadataFields: ['impugnedOrderDate', 'nabRegion', 'accusedName', 'allegationType']
    },
    {
      id: 'NAB Miscellaneous Application',
      label: 'NAB Miscellaneous Application',
      shortLabel: 'Application',
      description: 'Connected or standalone applications in NAB/accountability proceedings, including exemption, adjournment, recall of witness, production of record, de-freezing, custody, medical examination, transfer, stay, condonation, certified copies, or early hearing.',
      legalBasis: [
        'National Accountability Ordinance, 1999 / CrPC / QSO / constitutional context where applicable.'
      ],
      subtypes: [
        'Exemption from Appearance',
        'Application for Recall of Witness',
        'Application for Production of Record',
        'Application for De-Freezing / Release of Property',
        'Application for Medical Examination',
        'Application for Transfer of Case',
        'Application for Stay',
        'Application for Condonation of Delay',
        'Application for Early Hearing',
        'Application for Certified Copies',
        'Other NAB Application'
      ],
      forumOptions: [
        { label: 'Same Accountability Court Where Reference Is Pending', description: 'For applications connected with pending NAB reference/trial.', isRecommended: true },
        { label: 'Accountability Court', description: 'For reference, custody, evidence, record, and trial-connected applications.', isRecommended: false },
        { label: 'NAB Regional Office / Investigation Wing', description: 'For applications/representations connected with inquiry/investigation.', isRecommended: false },
        { label: 'High Court', description: 'For applications connected with bail, appeal, writ, or challenge.', isRecommended: false },
        { label: 'Other / Custom Forum', description: 'Use if forum differs.', isRecommended: false }
      ],
      sideOptions: ['Applicant', 'Respondent', 'Accused', 'NAB / Prosecution', 'Affected Person', 'Third-Party Claimant'],
      documentTabs: [
        'All',
        'Application',
        'Reply / Objections',
        'Supporting Documents',
        'NAB / Court Record',
        'Orders',
        'Miscellaneous'
      ],
      defaultStages: [
        'Application Preparation',
        'Filing',
        'Scrutiny / Objections, if any',
        'Notice to Respondent / NAB, if required',
        'Reply / Objections',
        'Record / Report Called, if required',
        'Hearing / Arguments',
        'Order',
        'Compliance / Main Case Linkage',
        'Closed'
      ],
      importantNotes: 'Application forum depends on the main NAB matter, pending forum, stage of proceedings, and nature of relief.',
      aiContextHints: 'AI should understand application subtype, pending NAB case, reference/inquiry/investigation status, accused role, record needed, custody/freezing/evidence issue, urgency, and order sought.',
      nabSpecificMetadataFields: ['accountabilityCourtCaseNumber', 'linkedReferenceCaseId', 'accusedName']
    },
    {
      id: 'Other NAB / Accountability Matter',
      label: 'Other NAB / Accountability Matter',
      shortLabel: 'Other NAB',
      description: 'Use this for NAB, accountability, corruption, public office, public funds, misuse of authority, assets beyond means, or accountability-related matters that do not fit the available NAB case types.',
      legalBasis: [],
      subtypes: [
        'Custom NAB Matter',
        'Custom Accountability Matter',
        'Other'
      ],
      forumOptions: [
        { label: 'NAB Regional Office / Investigation Wing', description: '', isRecommended: false },
        { label: 'Accountability Court', description: '', isRecommended: false },
        { label: 'High Court', description: '', isRecommended: false },
        { label: 'Supreme Court', description: '', isRecommended: false },
        { label: 'High Court / Constitutional Jurisdiction', description: '', isRecommended: false },
        { label: 'Other / Custom Forum', description: '', isRecommended: false }
      ],
      sideOptions: ['Party A', 'Party B', 'Accused', 'NAB / Prosecution', 'Applicant', 'Respondent', 'Affected Person'],
      documentTabs: [
        'All',
        'Documents',
        'Applications',
        'NAB Record',
        'Orders',
        'Miscellaneous'
      ],
      defaultStages: [
        'Case Preparation / Record Review',
        'Notice / Inquiry / Filing, if applicable',
        'Reply / Response',
        'Proceedings / Hearing',
        'Record / Evidence',
        'Arguments',
        'Order / Judgment',
        'Compliance / Further Remedy',
        'Closed'
      ],
      importantNotes: 'Forum depends on NAB stage, arrest/custody status, reference status, impugned order, statutory remedy, and maintainability.',
      aiContextHints: 'AI should rely on user-provided description, NAB record, allegation type, forum, uploaded documents, and selected relief.'
    }
  ]
};

export const normalizeNabCaseClassification = (existingCase: any) => {
  if (existingCase.caseCategory === 'NAB / Accountability' || existingCase.caseCategory === 'Accountability') {
    return { ...existingCase, caseCategory: 'NAB / Accountability' };
  }

  let type = existingCase.matterType || existingCase.caseTypeName || existingCase.caseType || '';
  let subtype = existingCase.caseSubtype || '';
  type = type.trim();
  subtype = subtype.trim();
  const lowerType = type.toLowerCase();

  const mapping: any = {
    'nab inquiry': ['NAB Inquiry / Call-Up Notice Matter'],
    'call-up notice': ['NAB Inquiry / Call-Up Notice Matter', 'Call-Up Notice'],
    'nab notice': ['NAB Inquiry / Call-Up Notice Matter', 'Inquiry Notice'],
    'nab questionnaire': ['NAB Inquiry / Call-Up Notice Matter', 'Questionnaire / Reply to NAB'],
    
    'nab investigation': ['NAB Investigation Matter'],
    'nab summons': ['NAB Investigation Matter', 'Summons / Appearance'],
    'nab arrest apprehension': ['NAB Investigation Matter', 'Arrest Apprehension'],
    
    'nab reference': ['NAB Reference / Trial'],
    'accountability reference': ['NAB Reference / Trial'],
    'corruption reference': ['NAB Reference / Trial', 'Corruption Reference'],
    'assets beyond means': ['NAB Reference / Trial', 'Assets Beyond Means Reference'],
    'misuse of authority': ['NAB Reference / Trial', 'Misuse of Authority Reference'],
    'supplementary reference': ['NAB Reference / Trial', 'Supplementary Reference'],
    
    'nab bail': ['NAB Bail'],
    'nab pre-arrest bail': ['NAB Bail', 'Pre-Arrest Bail'],
    'nab post-arrest bail': ['NAB Bail', 'Post-Arrest Bail'],
    'nab protective bail': ['NAB Bail', 'Protective Bail'],
    'nab bail after conviction': ['NAB Bail', 'Bail After Conviction'],
    
    'nab remand': ['Remand / Custody Matter'],
    'physical remand nab': ['Remand / Custody Matter', 'Physical Remand'],
    'judicial remand nab': ['Remand / Custody Matter', 'Judicial Remand'],
    'nab production order': ['Remand / Custody Matter', 'Production Order'],
    
    'plea bargain nab': ['Voluntary Return / Plea Bargain', 'Plea Bargain'],
    'voluntary return nab': ['Voluntary Return / Plea Bargain', 'Voluntary Return'],
    'vr nab': ['Voluntary Return / Plea Bargain', 'Voluntary Return'],
    
    'freezing of property nab': ['Freezing / Attachment of Property', 'Freezing of Immovable Property'],
    'freezing of bank account nab': ['Freezing / Attachment of Property', 'Freezing of Bank Account'],
    'attachment of property nab': ['Freezing / Attachment of Property', 'Attachment of Property'],
    'de-freezing application nab': ['Freezing / Attachment of Property', 'Release of Frozen Property'],
    
    'nab appeal': ['NAB Appeal'],
    'appeal against nab conviction': ['NAB Appeal', 'Appeal Against Conviction'],
    'appeal against accountability court judgment': ['NAB Appeal', 'Appeal Against Final Judgment'],
    'appeal by nab': ['NAB Appeal', 'Appeal by NAB'],
    'appeal by accused nab': ['NAB Appeal', 'Appeal by Accused'],
    
    'nab writ': ['NAB Revision / Constitutional Challenge'],
    'writ against nab': ['NAB Revision / Constitutional Challenge'],
    'challenge to nab inquiry': ['NAB Revision / Constitutional Challenge', 'Writ Against NAB Inquiry'],
    'challenge to nab investigation': ['NAB Revision / Constitutional Challenge', 'Writ Against NAB Investigation'],
    'challenge to nab reference': ['NAB Revision / Constitutional Challenge', 'Challenge to NAB Reference'],
    'challenge to nab arrest': ['NAB Revision / Constitutional Challenge', 'Challenge to NAB Arrest'],
    
    'nab miscellaneous application': ['NAB Miscellaneous Application'],
    'exemption in nab case': ['NAB Miscellaneous Application', 'Exemption from Appearance'],
    'recall witness nab': ['NAB Miscellaneous Application', 'Application for Recall of Witness'],
    'production of record nab': ['NAB Miscellaneous Application', 'Application for Production of Record'],
    'de-freezing application': ['NAB Miscellaneous Application', 'Application for De-Freezing / Release of Property'],
    
    'nab': ['Other NAB / Accountability Matter'],
    'nab case': ['Other NAB / Accountability Matter'],
    'accountability': ['Other NAB / Accountability Matter'],
    'accountability case': ['Other NAB / Accountability Matter']
  };

  const mapResult = mapping[lowerType];

  if (mapResult) {
    return {
      ...existingCase,
      caseCategory: 'NAB / Accountability',
      caseType: mapResult[0],
      caseSubtype: mapResult.length > 1 ? mapResult[1] : subtype,
      legacyCaseType: type
    };
  }
  
  if (lowerType.includes('nab ') || lowerType.includes('accountability')) {
      return {
          ...existingCase,
          caseCategory: 'NAB / Accountability',
          caseType: 'Other NAB / Accountability Matter',
          caseSubtype: type,
          legacyCaseType: type
      };
  }

  return existingCase;
};
